import { isEqual } from "lodash";
import { LOCATIONS_PLAN_TYPE } from "../consts/planTypes";

import { generateColor, processRoutes, processRoutesProfile } from "./routes";
import { copyOverlayDetails } from "./volumeData";

export const loadLocationsType = async ({
  routes,
  matrixProfile,
  volumeData,
  lookupTables,
}) => {
  const overlayData = volumeData && {
    overlayProfile: { ...volumeData.overlayProfile },
    overlayDetails: copyOverlayDetails(volumeData.overlayDetails),
  };

  const locationConfig = matrixProfile.locationConfig.map(
    (location, index) => ({
      ...location,
      ...generateColor(index),
    })
  );
  const locationCodesByRouteCode = locationConfig.reduce(
    (acc, { locationCode, routeCode }) => {
      if (!acc[routeCode]) acc[routeCode] = [];
      acc[routeCode].push(locationCode);
      return acc;
    },
    {}
  );
  routes = routes.map((route) => ({
    ...route,
    locationCodes: locationCodesByRouteCode[route.routeCode] || [],
  }));
  routes = processRoutes(routes, matrixProfile.routeConfig);

  const {
    routesWithVolume,
    routesByOuter,
    routesTimes,
    cutOffTimes,
    processedVolumeData,
  } = await processRoutesProfile({
    routeConfig: matrixProfile.routeConfig,
    redirectConfig: matrixProfile.redirectConfig,
    routes,
    locationConfig,
    overrideAreas: matrixProfile.overrideConfig,
    pickupAreas: matrixProfile.pickupConfig,
    volumeData: overlayData,
    lookupTables,
    planType: LOCATIONS_PLAN_TYPE,
  });

  return {
    ...matrixProfile,
    overrideAreas: matrixProfile.overrideConfig,
    locationConfig,
    routes: routesWithVolume,
    routesByOuter,
    routesTimes,
    cutOffTimes,
    volumeData: processedVolumeData,
  };
};

export const applyLocationChange = async ({
  locationCode,
  routeEdit,
  routeEditCopy,
  routesImpacted,
  routes,
  redirectConfig,
  routeConfigEdit,
  overrideAreas,
  pickupAreas,
  locationConfig,
  lookupTables,
  volumeData,
}) => {
  const { locationCodes } = routeEdit;

  let newLocationCodes;
  let newLocationConfig;
  let newRoutesImpacted = { ...routesImpacted };
  if (locationCodes.includes(locationCode)) {
    newLocationCodes = locationCodes.filter((code) => code !== locationCode);
    newLocationConfig = locationConfig.map((location) => {
      if (location.locationCode !== locationCode) return location;
      newRoutesImpacted[routeEdit.routeCode] = true;
      return { ...location, routeCode: null };
    });
  } else {
    newLocationCodes = [...locationCodes, locationCode];
    newLocationConfig = locationConfig.map((location) => {
      if (location.locationCode !== locationCode) return location;
      if (location.routeCode) newRoutesImpacted[location.routeCode] = true;
      newRoutesImpacted[routeEdit.routeCode] = true;
      return { ...location, routeCode: routeEdit.routeCode };
    });
  }

  const newRouteEdit = {
    ...routeEdit,
    locationCodes: newLocationCodes,
    locationRoute: !!newLocationCodes.length,
  };
  const newRoutes = routes.map((route) =>
    route.routeCode !== routeEdit.routeCode ? route : newRouteEdit
  );
  if (
    isEqual(
      [...newRouteEdit.locationCodes].sort(),
      [...routeEditCopy.locationCodes].sort()
    )
  )
    delete newRoutesImpacted[routeEdit.routeCode];

  const { routesWithVolume, processedVolumeData } = await processRoutesProfile({
    routeConfig: routeConfigEdit,
    redirectConfig,
    routes: newRoutes,
    volumeData,
    overrideAreas,
    pickupAreas,
    locationConfig: newLocationConfig,
    lookupTables,
    planType: LOCATIONS_PLAN_TYPE,
  });

  return {
    locationConfig: newLocationConfig,
    routeEdit: newRouteEdit,
    routes: routesWithVolume,
    routesImpacted: newRoutesImpacted,
    volumeData: processedVolumeData,
  };
};

export const getLocationVolume = ({ locationCode, volumes }) => {
  if (!volumes)
    return {
      stops: 0,
      parcels: 0,
    };
  else
    return {
      stops: volumes[locationCode]?.length || 0,
      parcels:
        volumes[locationCode]?.reduce(
          (acc, { stopParcels }) => acc + stopParcels,
          0
        ) || 0,
    };
};

export const getLocationsMap = (locationConfig) =>
  (locationConfig || []).reduce((acc, location) => {
    acc[location.locationCode] = location;
    return acc;
  }, {});
